import React from 'react';
import ListItemBase from '../ListItem/ListItemBase';
import Box from '../Box/Box';
import { InfoBadge, SuccessBadge } from '../Badge';
import { TextBodySmall, TextBodyBase } from '../Text';
import filterConditionalKeyValues from '../../utils/filterConditionalKeyValues';
import { useMenuItem } from './MenuContext';
import type { TMenuItem } from './types';

const MenuItem = ({
  icon,
  badgeLabel,
  successBadgeLabel,
  desktopOnlySuccessBadge,
  title,
  subtitle,
  onClick,
  selected,
  href,
  target,
  tabIndex,
  ariaLabel,
  ...rest
}: TMenuItem) => {
  const menuItem = useMenuItem();

  const handleClick = () => {
    onClick?.();
    menuItem?.close();
  };

  const createAriaLabel = () => {
    if (ariaLabel) {
      return ariaLabel;
    }

    if (subtitle && badgeLabel) {
      return `${title}. ${subtitle}. ${badgeLabel}`;
    }

    if (subtitle) {
      return `${title}. ${subtitle}`;
    }

    if (badgeLabel) {
      return `${title}. ${badgeLabel}`;
    }

    return title;
  };
  const isLink = !!href;
  const as = isLink ? 'a' : 'button';
  const conditionalProps = filterConditionalKeyValues({
    // link only props
    href: isLink ? href : undefined,
    target: isLink ? target : undefined,
    rel: isLink && target === '_blank' ? 'noopener noreferrer' : undefined,
    // selected aria for links should use aria-current
    'aria-current': isLink && selected ? 'page' : undefined,

    // button only props
    role: !isLink ? 'button' : undefined,
    type: !isLink ? 'button' : undefined,
    // buttons expose selection as a toggle; aria-selected is not valid on buttons
    'aria-pressed': !isLink && selected !== undefined ? String(selected) : undefined,
  });

  return (
    <ListItemBase as="li" kind="menu" modifiers={selected ? ['selected'] : []} {...rest}>
      <Box
        as={as}
        name="ListItemButton"
        ref={menuItem?.ref}
        {...(menuItem ? menuItem.getItemProps({ onClick: handleClick }) : { onClick: handleClick })}
        tabIndex={tabIndex}
        aria-label={createAriaLabel()}
        {...conditionalProps}
      >
        {icon && (
          <Box as="span" name="ListItem" aria-hidden="true" modifiers={['withIcon']}>
            {icon}
          </Box>
        )}
        <Box
          as="span"
          name="ListItem"
          aria-hidden="true"
          modifiers={['content', 'menuItem', ...(icon ? [] : ['noIcon'])]}
        >
          <Box as="span" name="TextContent">
            <TextBodyBase color={selected ? 'text-controls-selected' : 'text-primary'}>{title}</TextBodyBase>
            {Boolean(subtitle) && (
              <TextBodySmall color={selected ? 'text-controls-selected' : 'text-secondary'}>{subtitle}</TextBodySmall>
            )}
          </Box>
          {Boolean(badgeLabel) && <InfoBadge utilities={['marginLeftXxxs']}>{badgeLabel}</InfoBadge>}
          {Boolean(successBadgeLabel) && (
            <SuccessBadge className={desktopOnlySuccessBadge ? 'hz-hide-lte-md' : ''} utilities={['marginLeftXxxs']}>
              {successBadgeLabel}
            </SuccessBadge>
          )}
        </Box>
      </Box>
    </ListItemBase>
  );
};

export default MenuItem;
